import { useEffect } from "react";
import { useLocation } from "react-router";

const HIGHLIGHT_MS = 2000;
const WAIT_FRAMES = 120; // lazy pages: wait up to ~2s for the field to mount
const RING = ["outline-2", "outline-offset-4", "outline-focus", "rounded-md"] as const;

/**
 * OWN-365: on `#fieldId`, scroll to the field and ring it for two seconds (theme tokens only;
 * no smooth scroll under reduced motion).
 */
export function useSettingsHighlight(): void {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    const id = decodeURIComponent(hash.replace(/^#/, ""));
    if (!id) return;
    let frame = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let tries = 0;
    const find = () => {
      const el = document.getElementById(id);
      if (!el) {
        tries += 1;
        if (tries < WAIT_FRAMES) frame = requestAnimationFrame(find);
        return;
      }
      const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      el.scrollIntoView({ block: "center", behavior: reduced ? "auto" : "smooth" });
      el.classList.add(...RING);
      timer = setTimeout(() => el.classList.remove(...RING), HIGHLIGHT_MS);
    };
    frame = requestAnimationFrame(find);
    return () => {
      cancelAnimationFrame(frame);
      if (timer) clearTimeout(timer);
    };
  }, [hash, pathname]);
}
